import type { Food, Meal, MealOption, Pct, Plan, SlotKey, Template, Tip } from '../types';
import { BUILDER_SLOTS } from './builderMealSlots';
import { mealTargets, ratioFromOptions, sharesFromKcal, totals } from './nutrition';
import { plusDaysIso, todayIso } from './format';
import { PLAN_DURATION_DAYS } from './planDates';
import { getDietPlanWithNewKeys, getDietPlanWithOldKeys, optionTipsForV2Builder } from './payload';

export const MEAL_COLORS = ['#CBF0E0', '#299D6B', '#ABE6CC', '#246649', '#1D4D38', '#CBF0E0', '#299D6B'];

export const SLOTS = BUILDER_SLOTS;

export const PRESETS: { label: string; pct: Pct }[] = [
  { label: 'Balanced', pct: { p: 20, c: 50, f: 30 } },
  { label: 'High protein', pct: { p: 30, c: 40, f: 30 } },
  { label: 'Low carb', pct: { p: 25, c: 35, f: 40 } },
];

export const PROGRAM_TAG: Record<string, string> = {
  glp1: 'Weight loss',
  obesity: 'Weight loss',
  diabetes: 'Diabetes',
  prediabetes: 'Diabetes',
  pcos: 'PCOS',
  liver: 'Liver',
};

export function toTimeInput(t?: string) {
  if (!t) return '08:00';
  const s = String(t);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

export function toTimeApi(t?: string) {
  if (!t) return '08:00:00';
  const s = String(t);
  return s.length === 5 ? `${s}:00` : s;
}

/** Default meal end time: one hour after start (HH:mm). */
export const endTimeAfter = (hhmm: string) => {
  const [h, m] = toTimeInput(hhmm).split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0);
  d.setHours(d.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const slotKeyFor = (name: string): SlotKey => {
  const k = name.toLowerCase();
  if (/early morning/.test(k)) return 'early_morning';
  if (/bed\s*time|bedtime/.test(k)) return 'bed_time';
  if (/morning snack|mid/.test(k)) return 'morning_snacks';
  if (/evening/.test(k)) return 'evening_snacks';
  if (/breakfast/.test(k)) return 'breakfast';
  if (/lunch/.test(k)) return 'lunch';
  if (/dinner/.test(k)) return 'dinner';
  return 'lunch';
};

const freshMeals = (): Meal[] =>
  SLOTS.map((s, i) => ({
    key: s.key,
    mealTypesId: s.mealTypesId,
    name: s.name,
    time: s.time,
    endTime: endTimeAfter(s.time),
    share: s.share,
    ratio: null,
    ov: {},
    color: MEAL_COLORS[i % MEAL_COLORS.length],
    skip: false,
    options: [],
  }));

const num = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const finiteOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Fields `upsert_meal_targets` stores. Null clears a target; a missing key would leave the old row. */
const mealTargetFields = (plan: Plan, meal: Meal) => {
  const t = mealTargets(plan, meal);
  if (meal.skip || !(t.k > 0)) {
    return { kcal: null, protein_g: null, carbs_g: null, fats_g: null };
  }
  return {
    kcal: round2(t.k),
    protein_g: round2(t.p),
    carbs_g: round2(t.c),
    fats_g: round2(t.f),
  };
};

type SavedMealTarget = {
  kcal: number;
  protein_g: number | null;
  carbs_g: number | null;
  fats_g: number | null;
};

/** Positive saved kcal is the coach's target. Null or missing keeps the share derived from foods. */
const savedMealTarget = (meal: any): SavedMealTarget | null => {
  const nested = meal?.meal_target;
  const src = nested != null && typeof nested === 'object' ? nested : meal;
  const kcal = finiteOrNull(src?.kcal);
  if (kcal == null || !(kcal > 0)) return null;
  return {
    kcal,
    protein_g: finiteOrNull(src?.protein_g),
    carbs_g: finiteOrNull(src?.carbs_g),
    fats_g: finiteOrNull(src?.fats_g),
  };
};

/** Share precise enough that rounding kcal back to 2 decimals reproduces the saved value. */
const shareFromSavedKcal = (planKcal: number, kcal: number) =>
  Math.round((kcal / planKcal) * 1_000_000) / 10_000;

const applySavedMealTarget = (slot: Meal, planKcal: number, saved: SavedMealTarget) => {
  if (!(planKcal > 0)) return;
  slot.share = shareFromSavedKcal(planKcal, saved.kcal);
  const ov: Meal['ov'] = {};
  if (saved.protein_g != null) ov.p = saved.protein_g;
  if (saved.carbs_g != null) ov.c = saved.carbs_g;
  if (saved.fats_g != null) ov.f = saved.fats_g;
  slot.ov = ov;
};

/** API food rows store macros as line totals for `quantity`; the builder keeps per-unit values in `per`. */
export const foodFromApiItem = (item: any): Food => {
  const qty = Math.max(0.25, num(item?.quantity, 1) || 1);
  const cal = num(item?.calories);
  const prot = num(item?.protein);
  const carbs = num(item?.carbs);
  const fats = num(item?.fats ?? item?.fat);
  return {
    id: item?.food_item_id || item?.FOOD_ID,
    name: item?.food_item_name || item?.food_name || item?.FOOD_NAME || '',
    qty,
    unit: item?.measure_name || item?.unit_option_name || item?.UNIT_NAME || '',
    per: [cal / qty, prot / qty, carbs / qty, fats / qty],
    measureId: item?.measure_id || item?.unit_option_id || item?.UNIT_ID,
  };
};

const tipFromLine = (line: string): Tip | null => {
  const heading = line.trim();
  return heading ? { heading, detail: '' } : null;
};

const tipsFromLines = (text: string): Tip[] =>
  text.split('\n').map(tipFromLine).filter((t): t is Tip => !!t);

/** Parse template/API tip payloads into heading + detail rows. */
export const tipsFromRaw = (raw: unknown): Tip[] => {
  if (Array.isArray(raw)) {
    return raw.flatMap((t) => {
      if (typeof t === 'string') return tipsFromLines(t);
      const heading = String(t?.heading ?? t?.headline ?? '').trim();
      const detail = String(t?.detail ?? t?.subtitle ?? t?.tips ?? '').trim();
      if (!heading && detail.includes('\n')) return tipsFromLines(detail);
      if (heading.includes('\n') && !detail) return tipsFromLines(heading);
      const tip = { heading, detail };
      return tip.heading || tip.detail ? [tip] : [];
    });
  }
  if (typeof raw === 'string' && raw.trim()) return tipsFromLines(raw);
  return [];
};

const optionFromApi = (option: any): MealOption => ({
  foods: (option?.food_items || option?.option_data || [])
    .filter((f: any) => f && f.is_deleted !== 'Y')
    .map(foodFromApiItem)
    .filter((f: Food) => f.name),
  tips: tipsFromRaw(option?.tips),
});

/** Build a draft plan from a template (or blank). */
export const planFromTemplate = (
  t: Template | null,
  patientFirst: string,
  dates?: { from: string; to: string },
): Plan => {
  const meals = freshMeals();
  const from = dates?.from || todayIso();
  let templateMealShares: number[] | null = null;
  let templateMealRatios: (Pct | null)[] | null = null;
  if (t) {
    t.meals.forEach((m) => {
      const slot =
        meals.find((x) => x.mealTypesId && m.mealTypesId === x.mealTypesId) ||
        meals.find((x) => x.key === slotKeyFor(m.name));
      if (slot) {
        if (m.startTime) slot.time = m.startTime;
        if (m.endTime) slot.endTime = m.endTime;
        m.options.forEach((o) =>
          slot.options.push({
            foods: o.foods.map((f) => ({ ...f })),
            tips: o.tips.map((x) => ({ ...x })),
          }),
        );
      }
    });
    const shares = t.shares ?? sharesFromKcal(meals.map((m) => ratioFromOptions(m.options)?.k ?? 0));
    const ratios =
      t.ratios ??
      meals.map((m) => {
        const r = ratioFromOptions(m.options);
        return r ? { p: r.p, c: r.c, f: r.f } : null;
      });
    templateMealShares = meals.map((m, i) => shares[i] ?? m.share);
    templateMealRatios = ratios.map(r => (r ? { ...r } : null));
    meals.forEach((m, i) => {
      m.share = templateMealShares![i];
      m.ratio = templateMealRatios![i];
      if (!m.options.length) m.skip = true;
    });
  }
  meals.forEach((m) => {
    if (!m.options.length) m.options.push({ foods: [], tips: [] });
  });
  return {
    name: t ? `${t.name.split(',')[0]} plan` : patientFirst ? `${patientFirst}'s plan` : 'New diet plan',
    from,
    to: dates?.to || plusDaysIso(from, PLAN_DURATION_DAYS),
    kcal: t?.goal ?? 1500,
    pct: t ? { ...t.pct } : { ...PRESETS[0].pct },
    macroPreset: t ? 'Template' : PRESETS[0].label,
    templateId: t?.id ?? null,
    templateName: t?.name ?? '',
    templatePct: t ? { ...t.pct } : null,
    templateMealShares,
    templateMealRatios,
    templateMealTimes: t ? meals.map(m => ({ start: m.time, end: m.endTime })) : null,
    split: t ? 'template' : 'standard',
    meals,
  };
};

/** Map legacy editor / AI autofill shape (old keys) into a V2 Plan. */
export const planFromLegacyDiet = (data: any, patientFirst: string): Plan => {
  const blank = planFromTemplate(null, patientFirst);
  if (!data) return blank;
  blank.name = data.diet_name || blank.name;
  if (data.start_date) blank.from = String(data.start_date).slice(0, 10);
  if (data.end_date) blank.to = String(data.end_date).slice(0, 10);
  blank.kcal = num(data.calories, blank.kcal);
  const p = num(data.proteins, blank.pct.p);
  const c = num(data.carbs, blank.pct.c);
  const f = num(data.fat, blank.pct.f);
  if (p + c + f > 0) {
    blank.pct = { p, c, f };
    blank.macroPreset = PRESETS.find(pr => pr.pct.p === p && pr.pct.c === c && pr.pct.f === f)?.label ?? null;
  }
  const savedBySlot = new Map<Meal, SavedMealTarget | null>();
  (data.meals || []).forEach((meal: any) => {
    const slot =
      blank.meals.find((m) => m.mealTypesId === meal.meal_types_id) ||
      blank.meals.find((m) => m.key === slotKeyFor(meal.meal_name || ''));
    if (!slot) return;
    slot.skip = meal.is_hidden === 'Y';
    if (meal.start_time) slot.time = toTimeInput(meal.start_time);
    if (meal.end_time) slot.endTime = toTimeInput(meal.end_time);
    slot.options = (meal.options || []).map(optionFromApi);
    if (!slot.options.length) slot.options.push({ foods: [], tips: [] });
    savedBySlot.set(slot, savedMealTarget(meal));
  });
  const shares = sharesFromKcal(
    blank.meals.map((m) => (m.skip ? 0 : ratioFromOptions(m.options)?.k ?? m.share)),
  );
  blank.meals.forEach((m, i) => {
    if (!m.skip) m.share = shares[i] || m.share;
    const saved = savedBySlot.get(m);
    if (!m.skip && saved) applySavedMealTarget(m, blank.kcal, saved);
  });
  return blank;
};

const applyApiOptionTips = (plan: Plan, apiMeals: any[]) => {
  apiMeals.forEach((meal: any) => {
    const slot =
      plan.meals.find((m) => m.mealTypesId === meal.meal_types_id) ||
      plan.meals.find((m) => m.key === slotKeyFor(meal.meal_name || meal.meal_type || ''));
    if (!slot) return;
    const apiOptions = meal.options || meal.meal_options || [];
    apiOptions.forEach((apiOpt: any, oi: number) => {
      if (slot.options[oi]) {
        slot.options[oi].tips = tipsFromRaw(optionTipsForV2Builder(apiOpt));
      }
    });
  });
};

/** Load a saved/copy/autofill plan into the v2 builder (structured tips from API rows). */
export const planFromDietPlanApi = (data: any, patientFirst: string): Plan => {
  const legacy = getDietPlanWithOldKeys(data);
  const plan = planFromLegacyDiet(legacy, patientFirst);
  if (data?.diet_plan_meal_rel?.length) {
    applyApiOptionTips(plan, data.diet_plan_meal_rel);
  }
  return plan;
};

export const mapApiTemplateListItem = (row: any): Template => {
  const p = num(row.protein ?? row.proteins, 20);
  const c = num(row.carbs, 50);
  const f = num(row.fat ?? row.fats, 30);
  return {
    id: row._id || row.id,
    name: row.template_name || row.plan_name || 'Template',
    goal: num(row.total_calories ?? row.calories, 1500),
    pct: { p, c, f },
    tag: row.tag || row.condition || 'All',
    mine: false,
    meals: [],
  };
};

export const mapApiTemplateDetail = (data: any): Template => {
  const base = mapApiTemplateListItem(data);
  const meals_data = data?.meals_data || data?.meals || [];
  base.meals = meals_data.map((meal: any) => ({
    name: meal.meal_name || meal.meal_type || '',
    mealTypesId: meal.meal_types_id,
    startTime: meal.start_time ? toTimeInput(meal.start_time) : undefined,
    endTime: meal.end_time ? toTimeInput(meal.end_time) : undefined,
    options: (meal.options || meal.meal_options || []).map((option: any) => ({
      foods: (option.food_items || option.option_data || [])
        .map(foodFromApiItem)
        .filter((f: Food) => f.name),
      tips: tipsFromRaw(option.tips),
    })),
  }));
  return base;
};

const optionTipsString = (tips: Tip[]) =>
  tips
    .map((t) => [t.heading, t.detail].filter(Boolean).join('\n'))
    .filter(Boolean)
    .join('\n\n');

/** Map a V2 plan into the legacy editor shape, then into CreateDietPlanNew keys. */
export const planToCreatePayload = (
  plan: Plan,
  opts: { patientId: string; status: string; dietPlanId?: string },
) => {
  const actual = planActualKcal(plan);

  const legacy = {
    diet_name: plan.name,
    patient_id: opts.patientId,
    start_date: plan.from,
    end_date: plan.to,
    calories: String(plan.kcal),
    actual_calories: String(Math.round(actual * 10) / 10),
    proteins: String(plan.pct.p),
    fat: String(plan.pct.f),
    carbs: String(plan.pct.c),
    status: opts.status,
    meals: plan.meals.map((meal, idx) => ({
      meal_types_id: meal.mealTypesId,
      meal_name: meal.name,
      is_hidden: meal.skip ? 'Y' : 'N',
      start_time: toTimeApi(meal.time),
      end_time: toTimeApi(meal.endTime || endTimeAfter(meal.time)),
      order_no: SLOTS[idx]?.orderNo ?? idx + 1,
      ...mealTargetFields(plan, meal),
      options: meal.skip
        ? []
        : meal.options
            .map((option, oi) => ({
              options_name: `Option ${oi + 1}`,
              order_no: oi + 1,
              tips: optionTipsString(option.tips),
              food_items: option.foods
                .filter((f) => f.name)
                .map((f) => ({
                  food_item_id: f.id,
                  food_item_name: f.name,
                  quantity: String(f.qty),
                  measure_id: f.measureId,
                })),
            }))
            .filter((o) => o.food_items.length > 0),
    })),
  };

  return getDietPlanWithNewKeys(legacy, opts.dietPlanId);
};

/** Max kcal across options per meal (same basis as diet-plan actual_calories). */
export const planActualKcal = (plan: Plan) =>
  plan.meals.reduce((sum, meal) => {
    if (meal.skip) return sum;
    const ks = meal.options.map((o) => totals(o.foods).k);
    return sum + (ks.length ? Math.max(...ks) : 0);
  }, 0);

/** Payload for CreateDietTemplate / UpdateTemplateList from a V2 plan. */
export const planToTemplatePayload = (plan: Plan, templateName: string) => {
  const actual = Math.round(planActualKcal(plan) * 10) / 10;
  const meals_data = plan.meals.map((meal, idx) => {
    const options = meal.skip
      ? []
      : meal.options.flatMap((option, oi) =>
          option.foods
            .filter((f) => f.id && f.name)
            .map((foodItem, k) => ({
              tips: optionTipsString(option.tips) || 'No Tips',
              option_no: String(oi + 1),
              food_item_id: foodItem.id,
              food_name: foodItem.name,
              quantity: String(foodItem.qty),
              unit_option_id: foodItem.measureId != null ? String(foodItem.measureId) : undefined,
              unit_option_name: foodItem.unit,
              order_no: k + 1,
            })),
        );
    return {
      meal_types_id: meal.mealTypesId,
      meal_name: meal.name,
      order_no: SLOTS[idx]?.orderNo ?? idx + 1,
      start_time: toTimeApi(meal.time),
      end_time: toTimeApi(meal.endTime || endTimeAfter(meal.time)),
      hide_meal: meal.skip ? 'Y' : 'N',
      options,
    };
  });

  return {
    template_name: templateName,
    actual_calories: actual,
    total_calories: plan.kcal,
    proteins: plan.pct.p,
    fat: plan.pct.f,
    carbs: plan.pct.c,
    meals_data,
  };
};

export const isDirty = (a: Plan, b: Plan) => JSON.stringify(a) !== JSON.stringify(b);
